import { type ReactNode } from 'react';

import { Box, Stack } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import HomeIndicator from '@/designs-dev/device/HomeIndicator';
import ScreenRoot from '@/components/wrapped/ScreenRoot';
import AccountBar, { type Account } from '@/karehero-app/access/AccountBar';
import { PAGE_HEADER_TOP } from '@/karehero-app/shell/layout';

// Shared shell for the access flow steps: left-aligned page header, content,
// and the stacked primary/Back footer. Matches the wireframe onboarding
// shell (24px gutter, PAGE_HEADER_TOP above the header, 16px between buttons).

const SCREEN_PX = 3;

const StepScreen = ({
  title,
  description,
  children,
  primaryLabel,
  onPrimary,
  isPrimaryDisabled = false,
  onBack,
  footnote,
  footnoteBelow = false,
  account,
  bar,
  overlay,
}: {
  title: string;
  description?: string;
  children?: ReactNode;
  primaryLabel: string;
  onPrimary: () => void;
  isPrimaryDisabled?: boolean;
  onBack?: () => void;
  /** Centred line above the footer buttons (e.g. a "Having trouble?" link). */
  footnote?: ReactNode;
  /** Under the buttons instead: for a link that is the other answer, not help. */
  footnoteBelow?: boolean;
  /**
   * Draw the app bar, with the avatar that opens the account sheet. Omit it and
   * the step has no bar, which is right up to the point an account exists.
   */
  account?: Account;
  /**
   * A contextual app bar above the content, for a step inside a named flow.
   * Mutually exclusive with `account` in practice: a step has one top bar.
   */
  bar?: ReactNode;
  /**
   * Something floating over the step: a toast, a notice.
   *
   * A slot rather than the caller rendering it themselves, because the screen
   * root is what it has to be positioned against and that belongs to this. A
   * MUI Snackbar is the wrong tool inside a device frame — it either portals to
   * the document body or measures itself against the viewport, and both land it
   * on the Loft page around the phone.
   */
  overlay?: ReactNode;
}) => (
  <ScreenRoot relative>
    <StatusBar />
    {bar}
    {account && <AccountBar {...account} />}
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        overflow: 'auto',
        px: SCREEN_PX,
        // No top padding of its own: PAGE_HEADER_TOP below is the gap, and
        // adding to it here is what made this screen sit lower than every
        // other one in the same flow.
        pb: 2,
      }}
    >
      <Box sx={{ pt: PAGE_HEADER_TOP }}>
        <Stack spacing={3}>
          <PageHeader title={title} description={description} variant='h2' />
          {children}
        </Stack>
      </Box>
    </Box>
    {overlay}

    <Box
      sx={{
        px: SCREEN_PX,
        pt: 1.5,
        pb: 0.5,
        bgcolor: 'background.default',
        borderTop: 1,
        borderColor: 'custom.neutral.200',
      }}
    >
      <Stack spacing={2}>
        {footnote && !footnoteBelow && (
          <Box sx={{ textAlign: 'center' }}>{footnote}</Box>
        )}
        <Button
          variant='primary'
          size='lg'
          isFullWidth
          ariaLabel={primaryLabel}
          isDisabled={isPrimaryDisabled}
          onPress={onPrimary}
        >
          {primaryLabel}
        </Button>
        {onBack && (
          <Button
            variant='secondary'
            size='lg'
            isFullWidth
            ariaLabel='Back'
            onPress={onBack}
          >
            Back
          </Button>
        )}
        {footnote && footnoteBelow && (
          <Box sx={{ textAlign: 'center' }}>{footnote}</Box>
        )}
      </Stack>
    </Box>
    <HomeIndicator />
  </ScreenRoot>
);

export default StepScreen;
